import { motion } from 'framer-motion';
import DustParticles from './DustParticles';
import { FaReact, FaNodeJs, FaJs, FaHtml5, FaCss3Alt, FaMobile, FaDatabase, FaFire, FaBootstrap, FaServer, FaPalette, FaWind, FaCat } from 'react-icons/fa6';
import { FaWordpress, FaGitlab } from 'react-icons/fa6';
import { SiTypescript, SiMongodb, SiTailwindcss, SiFramer } from 'react-icons/si';
import Bubbles from './Bubbles';
import './Skills.css';

const skills = [
  { name: 'React', icon: <FaReact className="w-8 h-8" />, level: 90 },
  { name: 'Bootstrap', icon: <FaBootstrap className="w-8 h-8" />, level: 48 },
  { name: 'Expo', icon: <FaMobile className="w-8 h-8" />, level: 91 },
  { name: 'UI Kitten', icon: <FaCat className="w-8 h-8 text-green-400" />, level: 68 },
  { name: 'TypeScript', icon: <SiTypescript className="w-8 h-8" />, level: 85 },
  { name: 'JavaScript', icon: <FaJs className="w-8 h-8" />, level: 90 },
  { name: 'Node.js', icon: <FaNodeJs className="w-8 h-8" />, level: 64 },
  { name: 'HTML5', icon: <FaHtml5 className="w-8 h-8" />, level: 95 },
  { name: 'CSS3', icon: <FaCss3Alt className="w-8 h-8" />, level: 90 },
  { name: 'MongoDB', icon: <SiMongodb className="w-8 h-8" />, level: 52 },
  { name: 'GitLab', icon: <FaGitlab className="w-8 h-8 text-green-500" />, level: 85 },
  { name: 'Firebase', icon: <FaFire className="w-8 h-8" />, level: 78 },
  { name: 'MySQL', icon: <FaDatabase className="w-8 h-8" />, level: 86 },
  { name: 'Tailwind CSS', icon: <SiTailwindcss className="w-8 h-8" />, level: 83 },
  { name: 'Framer Motion', icon: <SiFramer className="w-8 h-8" />, level: 80 },
  { name: 'Windsurf', icon: <FaWind className="w-8 h-8 text-sky-500" />, level: 96 },
  { name: 'WordPress', icon: <FaWordpress className="w-8 h-8 text-green-500" />, level: 75 },
];

const Skills = () => {
  type SkillSection = {
    title: string;
    skills: string[];
  };

  type Category = {
    title: string;
    icon: React.ReactNode;
    sections: SkillSection[];
    color: string;
  };

  const categories: Category[] = [
    {
      title: 'Backend',
      icon: <FaServer className="text-xl" />,
      sections: [
        {
          title: 'Systèmes & Données',
          skills: ['Node.js', 'MongoDB', 'Firebase', 'MySQL']
        }
      ],
      color: '#ff8866'
    },
    {
      title: 'Frontend',
      icon: <FaMobile className="text-xl" />,
      sections: [
        {
          title: 'React.js',
          skills: ['Tailwind CSS', 'Bootstrap']
        },
        {
          title: 'React Native',
          skills: ['Expo', 'UI Kitten']
        }
      ],
      color: '#ff9966'
    },
    {
      title: 'IA & Outils Modernes',
      icon: <FaPalette className="text-xl" />,
      sections: [
        {
          title: 'Outils',
          skills: ['GitLab', 'Windsurf']
        }
      ],
      color: '#ff9966'
    }
  ];

  const getSkillLevel = (skillName: string) => {
    const normalizedSkillName = skillName.trim();
    const skill = skills.find(s => s.name.toLowerCase() === normalizedSkillName.toLowerCase());
    return skill ? skill.level : 0;
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative z-10">
      <motion.div
        className="text-center mb-12 sm:mb-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2 
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Compétences
        </motion.h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 justify-items-center">
          {categories.map((category, index) => (
            <div key={index} className="card">
              <div className="content">
                <div className="front" data-category={category.title}>
                  <div className="back-content">
                    {category.icon}
                    <strong>{category.title}</strong>
                    <small className="mt-2 text-sm opacity-80">Voir les compétences</small>
                    {category.title === 'Frontend' && <Bubbles count={20} />}
                  </div>
                </div>
                <div className="back">
                  <div className="img">
                    <div className="circle" style={{ backgroundColor: category.color }}></div>
                    <div className="circle" id="right" style={{ backgroundColor: category.color }}></div>
                    <div className="circle" id="bottom" style={{ backgroundColor: category.color.replace(')', ', 0.8)').replace('rgb', 'rgba') }}></div>
                  </div>
                  <div className="front-content">
                    <div className="description">
                      <div className="title">
                        <p className="title">
                          <strong>{category.title}</strong>
                        </p>
                        <svg fillRule="nonzero" height="15px" width="15px" viewBox="0 0 256 256" xmlnsXlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg">
                          <g style={{ mixBlendMode: 'normal' }} textAnchor="middle" fontSize="inherit" fontWeight="normal" fontFamily="inherit" strokeDashoffset="0" strokeDasharray="" strokeMiterlimit="10" strokeLinejoin="miter" strokeLinecap="butt" strokeWidth="1" stroke="none" fillRule="nonzero" fill="#20c997">
                            <g transform="scale(8,8)">
                              <path d="M25,27l-9,-6.75l-9,6.75v-23h18z"></path>
                            </g>
                          </g>
                        </svg>
                      </div>
                      <div className="mt-2">
                        {category.sections.map((section, sectionIndex) => (
                          <div key={sectionIndex} className="skills-section">
                            <div className="skills-title">
                              <h4 className="text-center font-medium text-gray-200 mb-3">{section.title}</h4>
                            </div>
                            <div className="skills-container">
                              {section.skills.map((skillName, skillIndex) => {
                                const level = getSkillLevel(skillName);
                                return (
                                  <div key={`${sectionIndex}-${skillIndex}`} className="skill-item">
                                    <span className="skill-name">{skillName}</span>
                                    <div 
                                      className="skill-circle"
                                      style={{
                                        '--progress': level + '%',
                                        '--progress-color': '#10B981' /* Vert émeraude */
                                      } as React.CSSProperties}
                                      data-progress={level}
                                    />
                                    <span className="skill-percent">{level}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div 
        className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl shadow-green-500/10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <motion.h3 
          className="text-2xl font-bold mb-8 text-center bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Technologies Maîtrisées
        </motion.h3>
        
        <div className="relative w-full overflow-hidden py-4">
          <div className="relative flex">
            <motion.div 
              className="flex gap-8"
              animate={{
                x: ['0%', '-50%'],
              }}
              transition={{
                duration: 60,
                repeat: Infinity,
                ease: 'linear',
                repeatType: 'loop' as const
              }}
            >
              {[...skills, ...skills, ...skills].filter(skill => skill.name !== 'Windsurf').map((skill, index) => (
              <motion.div
                key={`${skill.name}-${index}`}
                className="flex-shrink-0 flex flex-col items-center p-3 sm:p-4 bg-gray-800/30 rounded-xl hover:border-green-500/30 transition-all duration-300 group w-20 sm:w-24"
                whileHover={{ 
                  y: -5, 
                  scale: 1.05,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  backgroundColor: 'rgba(16, 185, 129, 0.05)'
                }}
              >
                <div className="text-green-400 mb-1 sm:mb-2 group-hover:scale-110 transition-transform text-xl sm:text-2xl md:text-3xl">
                  {skill.icon}
                </div>
                <span className="text-xs text-center text-gray-300 font-medium group-hover:text-green-400 transition-colors whitespace-nowrap overflow-hidden text-ellipsis w-full px-1">
                  {skill.name}
                </span>
                <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-green-500/20 pointer-events-none transition-all duration-300" />
              </motion.div>
            ))}
          </motion.div>
          </div>
          
          {/* Masques de dégradé sur les bords */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-900 to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-900 to-transparent z-10 pointer-events-none" />
        </div>
      </motion.div>

      <motion.div 
        className="mt-12 bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl shadow-green-500/10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <motion.h3 
          className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          Apprentissage Continu
        </motion.h3>
        
        <div className="relative">
          {/* Particules de poussière subtiles en arrière-plan */}
          <DustParticles count={15} color="rgba(74, 222, 128, 0.1)" />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto relative z-10">
            <motion.div 
              className="space-y-4 sm:space-y-6 md:space-y-8 bg-gray-900/30 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-xl border border-gray-800/50 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-900/5 to-transparent rounded-xl pointer-events-none" />
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-green-400 flex items-center">
                <span className="mr-2">📚</span> En Cours d'Acquisition
              </h4>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Je consacre plusieurs heures par semaine à me perfectionner et à explorer de nouveaux horizons technologiques. Ma démarche s'appuie sur une combinaison de formations certifiantes, de projets concrets et de veille technologique.
              </p>
            </motion.div>

            <motion.div 
              className="space-y-4 sm:space-y-6 md:space-y-8 bg-gray-900/30 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-xl border border-gray-800/50 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 to-transparent rounded-xl pointer-events-none" />
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-400 flex items-center">
                <span className="mr-2">🎯</span> Prochaines Étapes
              </h4>
              
              <div className="space-y-4">
                {[
                  "Approfondir les bonnes pratiques de sécurité applicative",
                  "Explorer l'optimisation des performances web avancées",
                  "Développer des compétences en UX/UI avancé"
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start group"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="relative mr-3 mt-1">
                      <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping opacity-75 group-hover:animate-none" />
                      <div className="relative w-5 h-5 flex items-center justify-center bg-blue-500/20 rounded-full border border-blue-500/30 group-hover:bg-blue-500/30 transition-colors">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                      </div>
                    </div>
                    <span className="text-gray-300 text-sm sm:text-base group-hover:text-white transition-colors">
                      {step}
                    </span>
                  </motion.div>
                ))}
              </div>
              
              <motion.p 
                className="text-xs sm:text-sm text-gray-400 italic mt-4 sm:mt-6 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 1 }}
              >
                "L'apprentissage est un voyage sans fin dans le monde passionnant de la technologie."
              </motion.p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Skills;
